import api from '@/lib/axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import type { Customer, CustomerFilters, PaginatedResponse, Booking, Payment, CustomersListResponse, CustomerAPIResponse } from '@/types';

export interface CustomersParams extends CustomerFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Helper function to map API response to frontend format
function mapCustomerFromAPI(apiCustomer: CustomerAPIResponse): Customer {
  const stats = (apiCustomer as any).statistics;
  return {
    id: apiCustomer.id,
    name: apiCustomer.name,
    email: apiCustomer.email || undefined,
    phone: apiCustomer.phone,
    isVerified: apiCustomer.is_verified,
    isBlocked: !apiCustomer.is_active,
    totalBookings: (apiCustomer as any).total_bookings ?? stats?.total_bookings ?? 0,
    totalSpent: stats?.total_spent ?? 0,
    createdAt: apiCustomer.created_at,
    lastActive: apiCustomer.updated_at,
    state: apiCustomer.state ?? undefined,
  };
}

export const customersApi = {
  getAll: async (params: CustomersParams): Promise<PaginatedResponse<Customer>> => {
    const response = await api.get<CustomersListResponse>(API_ENDPOINTS.CUSTOMERS.LIST, {
      params: {
        page: params.page || 1,
        limit: params.limit || 20,
        search: params.search || undefined,
        status: params.status && params.status !== 'all' ? params.status : undefined,
        state: params.state && params.state !== 'all' ? params.state : undefined,
        activity: params.activity && params.activity !== 'all' ? params.activity : undefined,
      }
    });
    
    return {
      data: response.data.data.customers.map(mapCustomerFromAPI),
      pagination: {
        page: response.data.data.page,
        limit: params.limit || 20,
        total: response.data.data.total,
        totalPages: response.data.data.pages,
      },
    };
  },

  getStates: async (): Promise<Array<{ state: string; count: number }>> => {
    const response = await api.get(API_ENDPOINTS.USER_STATES, { params: { role: 'customer' } });
    const raw = (response.data as any)?.data ?? response.data;
    return Array.isArray(raw) ? raw : [];
  },

  getById: async (id: string): Promise<Customer> => {
    const response = await api.get(API_ENDPOINTS.CUSTOMERS.DETAIL(id));
    const raw = (response.data as any)?.data ?? response.data;
    return mapCustomerFromAPI(raw);
  },

  block: async (id: string): Promise<void> => {
    await api.post(API_ENDPOINTS.CUSTOMERS.BLOCK(id));
  },

  unblock: async (id: string): Promise<void> => {
    await api.post(API_ENDPOINTS.CUSTOMERS.UNBLOCK(id));
  },

  getBookings: async (id: string): Promise<Booking[]> => {
    const response = await api.get(API_ENDPOINTS.CUSTOMERS.BOOKINGS(id));
    const raw = (response.data as any)?.data ?? response.data;
    const bookings: any[] = raw.bookings ?? (Array.isArray(raw) ? raw : []);
    return bookings.map((b): Booking => ({
      id: b.id,
      bookingNumber: `BK-${b.id.slice(0, 8)}`,
      customer: { id, name: b.name || '', phone: b.mobile_number || '' },
      driver: b.driver ? { id: b.driver.id, name: b.driver.name, phone: b.driver.phone, vehicleNumber: 'N/A' } : undefined,
      pickup: { address: b.pickup_address, city: b.city || '', state: 'Odisha', pincode: '', lat: 0, lng: 0 },
      dropoff: { address: b.drop_address, city: b.city || '', state: 'Odisha', pincode: '', lat: 0, lng: 0 },
      vehicleType: (b.vehicle_type || b.service_category || 'bike') as any,
      status: b.status as any,
      distance: parseFloat(b.distance || '0'),
      duration: b.duration || 0,
      fare: {
        baseFare: 0,
        distanceCharge: 0,
        timeCharge: 0,
        total: parseFloat((b.final_fare && b.final_fare !== '0' ? b.final_fare : b.estimated_fare) || '0'),
      },
      payment: {
        method: (b.payments?.[0]?.payment_method ?? 'cash') as any,
        status: (b.payments?.[0]?.payment_status === 'completed' ? 'paid' : b.payments?.[0]?.payment_status ?? 'pending') as any,
      },
      createdAt: b.created_at,
      confirmedAt: b.acceptance_time || undefined,
      pickedUpAt: b.start_time || undefined,
      deliveredAt: b.completion_time || undefined,
      cancelledAt: b.status === 'cancelled' ? b.updated_at : undefined,
    }));
  },

  getPayments: async (id: string): Promise<Payment[]> => {
    const response = await api.get(API_ENDPOINTS.CUSTOMERS.PAYMENTS(id));
    const raw = (response.data as any)?.data ?? response.data;
    return raw.payments ?? (Array.isArray(raw) ? raw : []);
  },
};

// Mock data
export const mockCustomers: Customer[] = Array.from({ length: 50 }, (_, i) => ({
  id: String(i + 1),
  name: ['Rajesh Kumar', 'Priya Sharma', 'Amit Patra', 'Sunita Devi', 'Rahul Mishra'][i % 5],
  email: `customer${i + 1}@example.com`,
  phone: `98765${String(i).padStart(5, '0')}`,
  avatar: i % 3 === 0 ? `https://i.pravatar.cc/150?img=${i + 1}` : undefined,
  isVerified: i % 4 !== 0,
  isBlocked: i % 10 === 0,
  totalBookings: Math.floor(Math.random() * 100) + 1,
  totalSpent: Math.floor(Math.random() * 50000) + 1000,
  createdAt: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString(),
  lastActive: i % 2 === 0 ? new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString() : undefined,
}));
